import { useEffect, useState } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import {
  IBusinessToolsRequest,
  IItemQuantity,
  IItemSpend,
  IJobSpend,
  ITopSpendByDepartmentResponse,
  ITopSpendByItemQuantityResponse,
  ITopSpendByItemSpendResponse,
  ITopSpendByJobNameResponse,
  ITopSpendByStoreResponse,
} from '../models/business-tools';
import {
  TopSpendByDepartmentAPI,
  TopSpendByItemQuantityAPI,
  TopSpendByItemSpendAPI,
  TopSpendByJobNameAPI,
  TopSpendByStoreAPI,
} from '../api/oms/business-tools';

export const GetTopSpendByJobName = (
  requestData: IBusinessToolsRequest,
  setTopJobs?: (value: IJobSpend[]) => void,
) => {
  const [jobNameData, setJobNameData] =
    useState<ITopSpendByJobNameResponse | null>(null);
  const [jobNameError, setJobNameError] = useState<AxiosError | null>(null);
  const [jobNameLoaded, setJobNameLoaded] = useState(false);

  useEffect(() => {
    /**
     * Used to the spinner to show on each api call
     * @setJobNameLoaded
     */
    setJobNameLoaded(false);
    TopSpendByJobNameAPI(requestData)
      .then((response: AxiosResponse<ITopSpendByJobNameResponse>) => {
        setJobNameData(response.data);
        if (setTopJobs) {
          const topSpendJobs = response.data.businessToolsResponse.jobsSpend;
          setTopJobs(topSpendJobs.length > 0 ? topSpendJobs : []);
        }
      })
      .catch((error: AxiosError) => setJobNameError(error))
      .finally(() => setJobNameLoaded(true));
  }, [
    requestData.businessToolsRequest.startDate,
    requestData.businessToolsRequest.endDate,
    requestData.businessToolsRequest.jobName,
  ]);

  return { jobNameData, jobNameError, jobNameLoaded };
};

export const GetTopSpendByStore = (requestData: IBusinessToolsRequest) => {
  const [topSpendByStore, setTopSpendByStore] =
    useState<ITopSpendByStoreResponse | null>(null);
  const [topSpendByStoreError, setTopSpendByStoreError] =
    useState<AxiosError | null>(null);
  const [topSpendByStoreLoaded, setTopSpendByStoreLoaded] = useState(false);

  useEffect(() => {
    setTopSpendByStoreLoaded(false);
    TopSpendByStoreAPI(requestData)
      .then((response: AxiosResponse<ITopSpendByStoreResponse>) =>
        setTopSpendByStore(response.data),
      )
      .catch((error: AxiosError) => setTopSpendByStoreError(error))
      .finally(() => setTopSpendByStoreLoaded(true));
  }, [
    requestData.businessToolsRequest.startDate,
    requestData.businessToolsRequest.endDate,
    requestData.businessToolsRequest.jobName,
  ]);

  return { topSpendByStore, topSpendByStoreError, topSpendByStoreLoaded };
};

export const GetTopSpendByDepartment = (requestData: IBusinessToolsRequest) => {
  const [jobDepartmentData, setJobDepartmentData] =
    useState<ITopSpendByDepartmentResponse | null>(null);
  const [jobDepartmentError, setJobDepartmentError] =
    useState<AxiosError | null>(null);
  const [jobDepartmentLoaded, setJobDepartmentLoaded] = useState(false);

  useEffect(() => {
    setJobDepartmentLoaded(false);
    TopSpendByDepartmentAPI(requestData)
      .then((response: AxiosResponse<ITopSpendByDepartmentResponse>) =>
        setJobDepartmentData(response.data),
      )
      .catch((error: AxiosError) => setJobDepartmentError(error))
      .finally(() => setJobDepartmentLoaded(true));
  }, [
    requestData.businessToolsRequest.startDate,
    requestData.businessToolsRequest.endDate,
    requestData.businessToolsRequest.jobName,
  ]);

  return { jobDepartmentData, jobDepartmentError, jobDepartmentLoaded };
};

/**
 * These two api's will not have local hooks but will use state setters
 * from parent, this will update item spend with proper data.
 */
export const GetTopSpendByItemSpend = (
  requestData: IBusinessToolsRequest,
  setTopItems: (value: IItemSpend[] & IItemQuantity[]) => void,
  setTopItemsError: (value: AxiosError) => void,
  setTopItemsLoaded: (value: boolean) => void,
) => {
  setTopItemsLoaded(false);
  TopSpendByItemSpendAPI(requestData)
    .then((response: AxiosResponse<ITopSpendByItemSpendResponse>) =>
      setTopItems(response.data.businessToolsResponse.itemSpend),
    )
    .catch((error: AxiosError) => setTopItemsError(error))
    .finally(() => setTopItemsLoaded(true));
};

export const GetTopSpendByItemQuantity = (
  requestData: IBusinessToolsRequest,
  setTopItems: (value: IItemSpend[] & IItemQuantity[]) => void,
  setTopItemsError: (value: AxiosError) => void,
  setTopItemsLoaded: (value: boolean) => void,
) => {
  setTopItemsLoaded(false);
  TopSpendByItemQuantityAPI(requestData)
    .then((response: AxiosResponse<ITopSpendByItemQuantityResponse>) =>
      setTopItems(response.data.businessToolsResponse.itemQuantity),
    )
    .catch((error: AxiosError) => setTopItemsError(error))
    .finally(() => setTopItemsLoaded(true));
};
